/*  ========================================================================
    # Hooks - Use History Values
    ========================================================================  */

import { useEffect } from 'react';

/**
 *
 *
 * @param {object} defaultParams Defines the URL parameter names with their initial page load values that are to be used.
 * @param {func} onUpdateValues Callback function that will be executed when new values are retrieved from history.
 *
 * @returns A function that accepts an object of new values that should be pushed to the history
 */
export default function useHistoryValues(defaultParams, onUpdateValues) {
  // Provide a method to push new values to the history.
  const pushNewValues = params => {
    const originalUrlParams = new URLSearchParams(window.location.search);
    const urlParams = new URLSearchParams();

    Object.keys(params).map(key => {
      // Only consider parameters that are part of the original definition.
      if (Object.prototype.hasOwnProperty.call(defaultParams, key) && params[key]) {
        urlParams.append(key, params[key]);
      }
    });

    // Add original params that are not part of this hook usage.
    originalUrlParams.forEach((originalValue, originalKey) => {
      if (!Object.prototype.hasOwnProperty.call(defaultParams, originalKey)) {
        urlParams.append(originalKey, originalValue);
      }
    });

    window.history.pushState(Object.fromEntries(urlParams), undefined, `?${urlParams.toString()}`);
  };

  // Register event listener to react to history changes.
  const historyPopState = event => {
    if (event.state) {
      const updatedParams = {};
      Object.keys(event.state).map(key => {
        if (Object.prototype.hasOwnProperty.call(defaultParams, key)) {
          updatedParams[key] = event.state[key];
        }
      });
      onUpdateValues(updatedParams);
    } else {
      // Reset back to initial page load cause state is undefined.
      onUpdateValues(defaultParams);
    }
  };

  useEffect(() => {
    window.addEventListener('popstate', historyPopState);
    return () => window.removeEventListener('popstate', historyPopState);
  }, []);

  return pushNewValues;
}
